import { useState } from 'react';
import { updateCampaignData } from '../utils/firebase';
import { processWorkflowStepSimple, createRestartAnalysisStateSimple } from './utils/workflowStepProcessorSimple';
import { 
  UseWorkflowAnalysisProps, 
  UseWorkflowAnalysisReturn,
  WorkflowConstants 
} from './types/workflowAnalysisTypes';

const WORKFLOW_CONSTANTS: WorkflowConstants = {
  MAX_RETRIES: 3,
  RETRY_DELAY: 2000
};

export const useWorkflowAnalysisSimple = ({ 
  formData, 
  initialSteps 
}: UseWorkflowAnalysisProps): UseWorkflowAnalysisReturn => {
  const [campaignId, setCampaignId] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [steps, setSteps] = useState(initialSteps || []);
  const [retryCount, setRetryCount] = useState(0);
  const [shouldStartAnalysis, setShouldStartAnalysis] = useState(false);

  const processStep = async () => {
    console.log('🎬 useWorkflowAnalysisSimple.processStep called with state:', {
      campaignId: !!campaignId,
      isProcessing,
      currentStep,
      totalSteps: steps.length,
      isComplete,
      shouldStartAnalysis,
      retryCount,
      timestamp: new Date().toISOString()
    });

    if (!campaignId) {
      console.log('❌ processStep: No campaignId, skipping');
      return;
    }
    
    if (isProcessing) {
      console.log('⏳ processStep: Already processing, skipping');
      return;
    }
    
    if (currentStep > steps.length) {
      console.log('🏁 processStep: Current step exceeds total steps, should be complete');
      return;
    }
    
    if (isComplete) {
      console.log('✅ processStep: Analysis already complete, skipping');
      return;
    }

    console.log('🚀 processStep: Starting step execution for step:', currentStep);
    setIsProcessing(true);

    console.log('📋 Setting step status to loading...');
    setSteps(prevSteps =>
      prevSteps.map(s =>
        s.id === currentStep ? { ...s, status: 'loading' as const } : s
      )
    );

    console.log('🔄 Calling processWorkflowStepSimple...');
    const result = await processWorkflowStepSimple({
      campaignId,
      currentStep,
      steps,
      formData,
      retryCount,
      maxRetries: WORKFLOW_CONSTANTS.MAX_RETRIES,
      retryDelay: WORKFLOW_CONSTANTS.RETRY_DELAY
    });

    console.log('📊 processWorkflowStepSimple result:', {
      success: result.success,
      shouldRetry: result.shouldRetry,
      hasUpdatedSteps: !!result.updatedSteps,
      nextStep: result.nextStep,
      isComplete: result.isComplete,
      error: result.error
    });

    if (result.success) {
      console.log('✅ Step processing successful');
      if (result.updatedSteps) {
        console.log('📋 Updating steps state with', result.updatedSteps.length, 'steps');
        setSteps(result.updatedSteps);
      }
      if (result.nextStep !== undefined) {
        console.log('➡️  Moving to next step:', result.nextStep);
        setCurrentStep(result.nextStep);
      }
      if (result.isComplete !== undefined) {
        console.log('🏁 Setting complete status:', result.isComplete);
        setIsComplete(result.isComplete);
        if (result.isComplete) {
          console.log('🛑 Analysis complete, stopping shouldStartAnalysis');
          setShouldStartAnalysis(false);
        }
      }
      setRetryCount(0);
      setIsProcessing(false);
      
      // Keep analysis running if not complete
      if (!result.isComplete && result.nextStep && result.nextStep <= steps.length) {
        console.log('🔄 Keeping shouldStartAnalysis true for next step:', result.nextStep);
        setShouldStartAnalysis(true);
      } else if (result.isComplete) {
        console.log('🎉 Analysis completed, setting shouldStartAnalysis to false');
        setShouldStartAnalysis(false);
      }
    } else if (result.shouldRetry) {
      console.log(`🔄 Step failed but should retry (attempt ${retryCount + 1}/${WORKFLOW_CONSTANTS.MAX_RETRIES})`);
      setRetryCount(prev => prev + 1);
      setTimeout(() => {
        console.log('⏰ Retry timeout completed, setting isProcessing to false');
        setIsProcessing(false);
      }, WORKFLOW_CONSTANTS.RETRY_DELAY);
    } else {
      console.log('❌ Step failed with no retry');
      if (result.updatedSteps) {
        console.log('📋 Updating steps with error state');
        setSteps(result.updatedSteps);
      }
      setRetryCount(0);
      setIsProcessing(false);
      setShouldStartAnalysis(false); // Stop processing on permanent failure
    }
  };

  const restartAnalysis = async () => {
    console.log('restartAnalysis: Restarting workflow');
    setCurrentStep(1);
    setIsComplete(false);
    setSteps(steps.map(step => ({
      ...step,
      status: 'pending' as const,
      result: ''
    })));
    setIsProcessing(false);
    setShouldStartAnalysis(true);

    if (campaignId) {
      const restartState = createRestartAnalysisStateSimple(steps);
      await updateCampaignData(campaignId, {
        analysisState: restartState
      });
    }
  };

  return {
    campaignId,
    setCampaignId,
    currentStep,
    setCurrentStep,
    isProcessing,
    isComplete,
    setIsComplete,
    steps,
    setSteps,
    shouldStartAnalysis,
    setShouldStartAnalysis,
    processStep,
    restartAnalysis
  };
};
